import React from "react";

const sectionClass = "text-2xl font-semibold mt-6 mb-2";
const subSectionClass = "text-xl font-semibold mt-4 mb-2";
const paragraphClass = "mb-4";
const listClass = "list-disc pl-6 mb-4 space-y-2";

const renderInline = (text) => {
  const segments = text.split(
    /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|[\w.+-]+@appazzola\.com|https?:\/\/[^\s]+)/g,
  );

  return segments.map((segment, index) => {
    if (/^[\w.+-]+@appazzola\.com$/.test(segment)) {
      return <a key={index} className="text-teal-600 underline" href={`mailto:${segment}`}>{segment}</a>;
    }

    if (segment.startsWith("http://") || segment.startsWith("https://")) {
      const href = segment.replace(/[.,;:)]$/, "");
      const suffix = segment.slice(href.length);

      return (
        <React.Fragment key={index}>
          <a className="text-teal-600 underline" href={href} target="_blank" rel="noopener noreferrer">
            {href}
          </a>
          {suffix}
        </React.Fragment>
      );
    }

    if (segment.startsWith("**") && segment.endsWith("**")) {
      return <strong key={index}>{segment.slice(2, -2)}</strong>;
    }

    if (segment.startsWith("*") && segment.endsWith("*")) {
      return <em key={index}>{segment.slice(1, -1)}</em>;
    }

    if (segment.startsWith("`") && segment.endsWith("`")) {
      return (
        <code key={index} className="rounded bg-slate-100 px-1.5 py-0.5 text-sm">
          {segment.slice(1, -1)}
        </code>
      );
    }

    return segment;
  });
};

const flushParagraph = (blocks, paragraphLines, key) => {
  if (paragraphLines.length === 0) {
    return;
  }

  blocks.push(
    <p key={key.current++} className={paragraphClass}>
      {renderInline(paragraphLines.join(" "))}
    </p>,
  );
  paragraphLines.length = 0;
};

const flushList = (blocks, listItems, key) => {
  if (listItems.length === 0) {
    return;
  }

  blocks.push(
    <ul key={key.current++} className={listClass}>
      {listItems.map((item, index) => (
        <li key={index}>{renderInline(item)}</li>
      ))}
    </ul>,
  );
  listItems.length = 0;
};

const flushTable = (blocks, tableLines, key) => {
  if (tableLines.length === 0) {
    return;
  }

  const rows = tableLines
    .filter((line) => !/^\|\s*-/.test(line))
    .map((line) =>
      line
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim()),
    );

  const [headers, ...bodyRows] = rows;

  blocks.push(
    <div key={key.current++} className="overflow-x-auto mb-4">
      <table className="w-full border-collapse text-left">
        {headers && (
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="border-b border-gray-300 pb-2 pr-4 align-top">
                  {renderInline(header)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="border-b border-gray-200 py-2 pr-4 align-top">
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
  );
  tableLines.length = 0;
};

const LegalMarkdown = ({ markdown }) => {
  const blocks = [];
  const paragraphLines = [];
  const listItems = [];
  const tableLines = [];
  const key = { current: 0 };

  markdown.split("\n").forEach((line) => {
    if (line.startsWith("# ")) {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      flushTable(blocks, tableLines, key);
      blocks.push(
        <h1 key={key.current++} className="text-4xl font-bold mb-6">
          {renderInline(line.slice(2))}
        </h1>,
      );
      return;
    }

    if (line.startsWith("## ")) {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      flushTable(blocks, tableLines, key);
      blocks.push(
        <h2 key={key.current++} className={sectionClass}>
          {renderInline(line.slice(3))}
        </h2>,
      );
      return;
    }

    if (line.startsWith("### ")) {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      flushTable(blocks, tableLines, key);
      blocks.push(
        <h3 key={key.current++} className={subSectionClass}>
          {renderInline(line.slice(4))}
        </h3>,
      );
      return;
    }

    if (line.trim() === "---") {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      flushTable(blocks, tableLines, key);
      blocks.push(<hr key={key.current++} className="my-6 border-gray-200" />);
      return;
    }

    if (line.startsWith("|")) {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      tableLines.push(line);
      return;
    }

    if (line.startsWith("- ")) {
      flushParagraph(blocks, paragraphLines, key);
      flushTable(blocks, tableLines, key);
      listItems.push(line.slice(2));
      return;
    }

    if (line.trim() === "") {
      flushParagraph(blocks, paragraphLines, key);
      flushList(blocks, listItems, key);
      flushTable(blocks, tableLines, key);
      return;
    }

    flushList(blocks, listItems, key);
    flushTable(blocks, tableLines, key);
    paragraphLines.push(line);
  });

  flushParagraph(blocks, paragraphLines, key);
  flushList(blocks, listItems, key);
  flushTable(blocks, tableLines, key);

  return blocks;
};

export default LegalMarkdown;
